import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, ShieldAlert } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Frequency -> how long a job can go without a fresh "executed" event before
// it reads as stale rather than just "hasn't been due yet." A generous 3x
// multiplier absorbs normal jitter (a slow prior task pushing this one back
// a few minutes) without flagging every job as broken on every page load;
// this is deliberately loose since the real intent is catching what this
// session's own celery outage looked like (a job silently stopping for
// HOURS), not nitpicking minute-level scheduling variance.
const FREQUENCY_MINUTES = {
  every_5_minutes: 5,
  every_30_seconds: 1,
  hourly: 60,
  daily: 24 * 60,
  weekly: 7 * 24 * 60,
  monthly: 30 * 24 * 60,
};
const STALE_MULTIPLIER = 3;
const STALE_FLOOR_MINUTES = 15;

function humanizeFrequency(freq) {
  if (!freq) return "—";
  return freq.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

// job_events timestamps come back with an explicit "+00:00" offset already
// (not "Z") -- blindly appending "Z" like a bare-naive-timestamp field would
// produce "...+00:00Z", an invalid double-suffixed string that silently
// parses to Invalid Date (NaN everywhere downstream). Only append when
// neither a "Z" nor an explicit offset is already present.
function parseUtc(iso) {
  if (!iso) return null;
  const hasOffset = iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso);
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Same helper for both directions -- a job's last-run time is always in
// the past ("3m ago"), but a pending job's next-run time can be in the
// future ("in 3m") just as easily as overdue ("3m ago", the stuck case).
function relTime(iso) {
  const d = parseUtc(iso);
  if (!d) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  const future = mins < 0;
  const abs = Math.abs(mins);
  if (abs < 1) return "just now";
  if (abs < 60) return future ? `in ${abs}m` : `${abs}m ago`;
  const hrs = Math.round(abs / 60);
  if (hrs < 24) return future ? `in ${hrs}h` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

function fmtDuration(minutes) {
  if (minutes == null) return "—";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hrs = Math.floor(minutes / 60);
  const rem = Math.round(minutes % 60);
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

// Real health computed from scheduled_jobs + job_events, the same tables
// Celery beat/worker actually write to -- no separate "integration status"
// concept exists anywhere else. This is deliberately the thing that
// would have caught this session's own celery-outage bug (a job silently
// stopping for 21+ hours) at a glance instead of by accident.
function computeHealth(job, latestEvent) {
  if (!job.active) return { level: "inactive", label: "Inactive" };
  if (!latestEvent) return { level: "warning", label: "Never run" };
  if (latestEvent.status === "executed") {
    const staleAfterMinutes = Math.max(
      STALE_FLOOR_MINUTES,
      (FREQUENCY_MINUTES[job.frequency] || 60) * STALE_MULTIPLIER
    );
    const executedAt = parseUtc(latestEvent.executed_at);
    const ageMinutes = executedAt ? (Date.now() - executedAt.getTime()) / 60000 : Infinity;
    if (ageMinutes > staleAfterMinutes) return { level: "critical", label: "Stale" };
    return { level: "healthy", label: "Healthy" };
  }
  if (latestEvent.status === "pending") {
    const executionDate = parseUtc(latestEvent.execution_date);
    const overdueMinutes = executionDate ? (Date.now() - executionDate.getTime()) / 60000 : Infinity;
    if (overdueMinutes > STALE_FLOOR_MINUTES) return { level: "critical", label: "Stuck" };
    return { level: "healthy", label: "Scheduled" };
  }
  return { level: "warning", label: latestEvent.status };
}

const LEVEL_META = {
  healthy: { color: "#16a34a", bg: "rgba(22,163,74,0.08)", icon: CheckCircle2 },
  warning: { color: "#a16207", bg: "rgba(234,179,8,0.1)", icon: AlertTriangle },
  critical: { color: "#dc2626", bg: "rgba(220,38,38,0.08)", icon: XCircle },
  inactive: { color: "var(--muted)", bg: "var(--bg)", icon: Clock },
};

export default function Integrations() {
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real Motive sync outage history (motive_sync_incidents, backend/
  // motive_sync.py's _check_motive_sync_incidents) -- distinct from the
  // job-health cards above, which only ever show scheduled_jobs' current
  // state. A sync job can read "Healthy" right now while having been down
  // for hours yesterday; this is the only place that fact survives.
  const [reliability, setReliability] = useState(null);
  const [reliabilityLoading, setReliabilityLoading] = useState(true);
  const [reliabilityError, setReliabilityError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${apiBaseUrl}/scheduled_jobs/?limit=100`, authHeaders()),
      axios.get(`${apiBaseUrl}/job_events/?limit=300&sort_by=execution_date&sort_order=desc`, authHeaders()),
    ])
      .then(([jobRes, eventRes]) => {
        setJobs(jobRes.data.records || jobRes.data || []);
        setEvents(eventRes.data.records || eventRes.data || []);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));

    setReliabilityLoading(true);
    axios.get(`${apiBaseUrl}/scripted/fleet/motive-sync-reliability`, authHeaders())
      .then((res) => setReliability(res.data))
      .catch((err) => setReliabilityError(err.response?.data?.detail || err.message))
      .finally(() => setReliabilityLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const latestByJobId = {};
  events.forEach((e) => {
    const jobId = e.job_id?.id || e.job_id;
    if (!latestByJobId[jobId]) latestByJobId[jobId] = e;
  });

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Integrations</h1>
          <div style={styles.subtitle}>
            Health of every scheduled background job (Motive sync, live ETA tracking, and any others).
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={load} disabled={loading}>
          <RefreshCw size={13} style={loading ? { animation: "lv-spin 0.8s linear infinite" } : undefined} /> Refresh
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load integration status: {error}</div>}

      <div style={styles.list}>
        {jobs.map((job) => {
          const latest = latestByJobId[job.id];
          const health = computeHealth(job, latest);
          const meta = LEVEL_META[health.level];
          const Icon = meta.icon;
          return (
            <div key={job.id} style={styles.card}>
              <div style={{ ...styles.icon, color: meta.color, background: meta.bg }}>
                <Icon size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.cardTop}>
                  <span style={styles.jobName}>{job.name}</span>
                  <span style={{ ...styles.pill, color: meta.color, background: meta.bg }}>{health.label}</span>
                </div>
                <div style={styles.jobMeta}>
                  {humanizeFrequency(job.frequency)}
                  {latest?.status === "executed" && ` · last ran ${relTime(latest.executed_at)}`}
                  {latest?.status === "pending" && ` · next run due ${relTime(latest.execution_date)}`}
                  {!latest && " · no events recorded yet"}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && !error && jobs.length === 0 && (
          <div style={styles.emptyState}>No scheduled jobs configured.</div>
        )}
      </div>

      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>Motive Sync Reliability</h2>
        <div style={styles.sectionSubtitle}>
          Real outage history, not just current status -- every gap in successful syncs over the last ~6 months.
        </div>
      </div>

      {reliabilityError && <div style={styles.errorBox}>Couldn't load sync reliability: {reliabilityError}</div>}

      <div style={styles.reliabilyStatRow}>
        <div style={styles.reliabilityStat}>
          <div style={styles.reliabilityStatValue}>{reliabilityLoading ? "…" : reliability?.incident_count ?? 0}</div>
          <div style={styles.reliabilityStatLabel}>Outages logged</div>
        </div>
        <div style={styles.reliabilityStat}>
          <div style={styles.reliabilityStatValue}>
            {reliabilityLoading ? "…" : fmtDuration(reliability?.total_downtime_minutes)}
          </div>
          <div style={styles.reliabilityStatLabel}>Total downtime</div>
        </div>
        <div style={styles.reliabilityStat}>
          <div style={styles.reliabilityStatValue}>
            {reliabilityLoading ? "…" : fmtDuration(reliability?.longest_incident_minutes)}
          </div>
          <div style={styles.reliabilityStatLabel}>Longest single outage</div>
        </div>
      </div>

      {!reliabilityLoading && !reliabilityError && (reliability?.incidents?.length || 0) === 0 && (
        <div style={styles.emptyState}>No Motive sync outages logged in this range -- every tick has completed on time.</div>
      )}

      {(reliability?.incidents?.length || 0) > 0 && (
        <div style={styles.list}>
          {reliability.incidents.map((inc) => (
            <div key={inc.id} style={styles.card}>
              <div style={{ ...styles.icon, color: LEVEL_META.critical.color, background: LEVEL_META.critical.bg }}>
                <ShieldAlert size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.cardTop}>
                  <span style={styles.jobName}>Down for {fmtDuration(inc.gap_minutes)}</span>
                </div>
                <div style={styles.jobMeta}>
                  {relTime(inc.started_at)} -- recovered {relTime(inc.resolved_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 520 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
  },
  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  card: {
    display: "flex", alignItems: "center", gap: 12, background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)", padding: "14px 16px", boxShadow: "var(--shadow-xs)",
  },
  icon: { width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  cardTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 2 },
  jobName: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)" },
  pill: { fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 999 },
  jobMeta: { fontSize: 12, color: "var(--muted)" },
  emptyState: {
    textAlign: "center", color: "var(--muted)", fontSize: 13, padding: 40,
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
  },

  sectionHead: { marginTop: 32, marginBottom: 14 },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: "var(--ink)", margin: 0 },
  sectionSubtitle: { fontSize: 12.5, color: "var(--muted)", marginTop: 3 },

  reliabilyStatRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 16 },
  reliabilityStat: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "14px 16px", boxShadow: "var(--shadow-xs)",
  },
  reliabilityStatValue: { fontSize: 18, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  reliabilityStatLabel: { fontSize: 11.5, color: "var(--muted)", marginTop: 2 },
};
